import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useNotifications } from '../context/NotificationContext';
import { formatRelativeTime } from '../utils/time';
import Button from '../components/ui/Button';

export default function Notifications() {
  const { notifications, loading, markAsRead, markAllAsRead } = useNotifications();
  const navigate = useNavigate();

  const hasUnread = notifications.some((n) => !n.is_read);

  const handleClick = (n) => {
    markAsRead(n.id);
    if (n.link) navigate(n.link);
  };

  return (
    <div className="min-h-screen bg-sand-50">
      <div className="max-w-2xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {/* Page header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl font-semibold text-ink-900">Notifications</h1>
            {notifications.length > 0 && (
              <p className="text-sm text-ink-400 mt-0.5">{notifications.length} total</p>
            )}
          </div>
          {hasUnread && (
            <Button variant="secondary" size="sm" onClick={markAllAsRead}>
              Mark all as read
            </Button>
          )}
        </div>

        {/* Loading skeletons */}
        {loading ? (
          <div className="space-y-px bg-white border border-sand-200 rounded-xl overflow-hidden">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="px-5 py-4 flex items-start gap-3 border-b border-sand-100 last:border-0">
                <div className="w-2 h-2 rounded-full bg-sand-200 mt-1.5 shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-3.5 bg-sand-200 rounded w-2/5 animate-pulse" />
                  <div className="h-3 bg-sand-100 rounded w-4/5 animate-pulse" />
                  <div className="h-3 bg-sand-100 rounded w-1/4 animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : notifications.length === 0 ? (
          /* Empty state */
          <div className="bg-white rounded-xl border border-sand-200 px-6 py-20 text-center">
            <div className="w-12 h-12 rounded-full bg-sand-100 flex items-center justify-center mx-auto mb-4">
              <Bell size={22} strokeWidth={1.5} className="text-sand-400" />
            </div>
            <p className="text-sm font-medium text-ink-700">You're all caught up</p>
            <p className="text-sm text-ink-400 mt-1">Notifications will appear here as activity happens.</p>
          </div>
        ) : (
          /* Notification list */
          <div className="bg-white rounded-xl border border-sand-200 overflow-hidden divide-y divide-sand-100">
            {notifications.map((n) => (
              <button
                key={n.id}
                onClick={() => handleClick(n)}
                className={`w-full text-left px-5 py-4 flex items-start gap-3 hover:bg-sand-50 transition-colors duration-150 ${!n.is_read ? 'bg-terra-50/40' : ''}`}
              >
                <span
                  className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${!n.is_read ? 'bg-terra-400' : 'bg-sand-200'}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <p className={`text-sm text-ink-800 ${!n.is_read ? 'font-semibold' : 'font-medium'}`}>
                      {n.title}
                    </p>
                    <p className="text-xs text-ink-400 shrink-0 mt-0.5">{formatRelativeTime(n.created_at)}</p>
                  </div>
                  <p className="text-sm text-ink-500 mt-0.5 leading-relaxed">{n.body}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
